import { AuthSession, type Account } from '../api/session';
import { isApiError } from '../api/client';
import { createJerseyPreview, type JerseySpec } from './jerseyPreview';
import { button, div, el, note, overlayPanel, textInput } from './dom';

// Account / teams UI (§13.1/§13.3). A hand-built DOM overlay over the game: Sign in → Account → Teams →
// Team detail (with a live jersey editor + invite links). All user strings render via textContent (§15.1).
//
// D157 (brand slice 1): this module no longer owns HOME — the persistent full-bleed home screen
// (ui/homeScreen.ts) sits beneath, and these are openable flows on top. "Back"/done paths therefore
// CLOSE the overlay rather than mounting a home card; the magic-link ?token and #join=CODE boot logic
// is preserved verbatim inside boot(). The flow bodies themselves are unchanged this slice beyond the
// retokened dom.ts palette — their proper redesigns are future handoffs of the brand brief.

export interface AccountFlowsOpts {
  onCreateOnline: (clubTeamId?: string) => void;
  /** The overlay closed (or boot's refresh settled): whatever sits beneath should re-read session state. */
  onStateChange?: () => void;
}

export interface AccountFlows {
  openSignIn(): void;
  openAccount(): void;
  openTeams(): void;
  /** Honour a magic-link `?token` or an `#join=CODE` deep link; otherwise leave the overlay closed. */
  boot(): void;
}

const PATTERNS = ['solid', 'stripes', 'hoops', 'halves', 'sash', 'quarters'] as const;
const CRESTS = Array.from({ length: 24 }, (_, i) => `crest-${String(i + 1).padStart(2, '0')}`);
const DEFAULT_JERSEY: JerseySpec = { pattern: 'stripes', primary: '#e63946', secondary: '#f1faee', crest: 'crest-01' };

interface TeamSummary {
  id: string;
  name: string;
  jersey: JerseySpec;
}

export function createAccountFlows(session: AuthSession, opts: AccountFlowsOpts): AccountFlows {
  const { overlay, mount } = overlayPanel();
  overlay.style.display = 'none'; // mounts closed — the home screen beneath is the landing view
  document.body.append(overlay);
  const open = (): void => void (overlay.style.display = 'flex');
  /** Close without notifying — for handing off into a match, where home must NOT re-show. */
  const dismiss = (): void => void (overlay.style.display = 'none');
  const close = (): void => {
    dismiss();
    opts.onStateChange?.();
  };

  const err = (e: unknown): string => (isApiError(e) ? `${e.code}: ${e.message}` : 'network error');
  const title = (): HTMLElement => {
    const h = div('display:flex;justify-content:space-between;align-items:baseline;margin-bottom:12px');
    h.append(div("font-family:var(--font-display);font-size:26px;letter-spacing:0.01em;color:var(--ink-1)", 'FAFI'));
    if (session.signedIn && session.account) h.append(div('font-size:12px;color:var(--ink-2)', session.account.displayName));
    return h;
  };

  // ── SIGN IN ──────────────────────────────────────────────────────────────────
  const signIn = (): void => {
    const c = div();
    c.append(title());
    c.append(div('font-size:18px;margin:8px 0', 'Sign in'));
    const email = textInput('you@example.com', '', 'email');
    const msg = note('');
    const send = button('Send magic link', () => {
      msg.textContent = 'sending…';
      session.api
        .post('/auth/magic-link', { email: email.value.trim() })
        .then(() => (msg.textContent = '✓ Check your email for the sign-in link (in dev, see the server console).'))
        .catch((e) => (msg.textContent = err(e)));
    }, { primary: true });
    c.append(email, send, msg);
    // dev/paste fallback: accept a magic-link token directly
    c.append(div('margin-top:14px;color:var(--ink-3);font-size:12px', 'Have a sign-in link/token? Paste it:'));
    const tok = textInput('token from the link');
    c.append(tok, button('Verify token', () => verifyToken(tok.value.trim())));
    c.append(div('margin-top:14px'), button('← Back', () => close()));
    mount(c);
  };

  const verifyToken = (token: string): void => {
    if (!token) return;
    session
      .verify(token)
      .then(() => account())
      .catch((e) => {
        const c = div();
        c.append(title(), note(`Sign-in failed — ${err(e)}`, 'var(--danger-ink)'), button('← Back', () => signIn()));
        mount(c);
      });
  };

  // ── ACCOUNT ──────────────────────────────────────────────────────────────────
  const account = (): void => {
    const a = session.account;
    if (!a) return void close();
    const c = div();
    c.append(title());
    c.append(div('font-size:18px;margin:8px 0', 'Account'), div('color:var(--ink-2);font-size:13px', a.email));
    c.append(div('margin-top:10px', 'Display name'));
    const name = textInput('display name', a.displayName);
    const msg = note('');
    c.append(
      name,
      button('Save name', () => {
        session.api
          .patch<{ account: Account }>('/me', { displayName: name.value.trim() })
          .then((r) => ((session.account = r.account), (msg.textContent = '✓ saved')))
          .catch((e) => (msg.textContent = err(e)));
      }),
      msg,
    );
    c.append(div('margin-top:16px'), button('👕 My teams', () => teams()), button('Sign out', () => session.signOut().then(() => close())));
    c.append(div('margin-top:16px'));
    c.append(button('Delete account', () => confirmDelete()));
    c.append(div('margin-top:12px'), button('← Back', () => close()));
    mount(c);
  };

  const confirmDelete = (): void => {
    const c = div();
    c.append(title(), note('Delete your account? Owned teams transfer to an admin/member, else are deleted. This cannot be undone.', 'var(--danger-ink)'));
    c.append(
      button('Yes, delete', () => session.api.delete('/me').then(() => (session.setToken(null), close())).catch(() => close())),
      button('Cancel', () => account()),
    );
    mount(c);
  };

  // ── TEAMS LIST ─────────────────────────────────────────────────────────────────
  const teams = (): void => {
    const c = div();
    c.append(title(), div('font-size:18px;margin:8px 0', 'My teams'));
    const list = div('margin:8px 0');
    list.append(note('loading…'));
    c.append(list);
    c.append(button('+ Create team', () => createTeam()), button('Join by code', () => joinTeam()), div('margin-top:12px'), button('← Back', () => close()));
    mount(c);
    session.api
      .get<{ teams: TeamSummary[] }>('/me/teams')
      .then((r) => {
        list.replaceChildren();
        if (r.teams.length === 0) list.append(note('No teams yet — create one to invite friends and play as a club.'));
        for (const t of r.teams) {
          const row = div('display:flex;align-items:center;gap:10px;margin:6px 0;padding:6px;border:1px solid var(--border-hairline);border-radius:8px;cursor:pointer');
          row.append(createJerseyPreview(t.jersey ?? DEFAULT_JERSEY, 40), div('flex:1', t.name));
          row.onclick = () => teamDetail(t.id);
          list.append(row);
        }
      })
      .catch((e) => (list.replaceChildren(note(err(e), 'var(--danger-ink)'))));
  };

  const joinTeam = (): void => {
    const c = div();
    c.append(title(), div('font-size:18px;margin:8px 0', 'Join a team'));
    const code = textInput('invite code');
    const msg = note('');
    c.append(code, button('Join', () => {
      session.api
        .post('/teams/join', { code: code.value.trim() })
        .then(() => teams())
        .catch((e) => (msg.textContent = err(e)));
    }, { primary: true }), msg, div('margin-top:12px'), button('← Back', () => teams()));
    mount(c);
  };

  // ── CREATE TEAM (jersey editor) ─────────────────────────────────────────────────
  const createTeam = (): void => {
    const jersey: JerseySpec = { ...DEFAULT_JERSEY };
    const c = div();
    c.append(title(), div('font-size:18px;margin:8px 0', 'Create team'));
    const name = textInput('team name');
    const preview = div('display:flex;justify-content:center;margin:10px 0');
    const redraw = (): void => preview.replaceChildren(createJerseyPreview(jersey, 140));
    c.append(name, preview);
    c.append(editorControls(jersey, redraw));
    const msg = note('');
    c.append(
      button('Create', () => {
        session.api
          .post<{ team: { id: string } }>('/teams', { name: name.value.trim(), jersey })
          .then((r) => teamDetail(r.team.id))
          .catch((e) => (msg.textContent = err(e)));
      }, { primary: true }),
      button('← Back', () => teams()),
      msg,
    );
    mount(c);
    redraw();
  };

  const editorControls = (jersey: JerseySpec, redraw: () => void): HTMLElement => {
    const wrap = div();
    const patternRow = div('margin:6px 0');
    patternRow.append(div('font-size:12px;color:var(--ink-3)', 'Pattern'));
    for (const p of PATTERNS) patternRow.append(button(p, () => ((jersey.pattern = p), rebuild()), { active: jersey.pattern === p }));
    const colorRow = div('display:flex;gap:12px;align-items:center;margin:8px 0');
    const primary = el('input');
    primary.type = 'color';
    primary.value = jersey.primary;
    primary.oninput = () => ((jersey.primary = primary.value), redraw());
    const secondary = el('input');
    secondary.type = 'color';
    secondary.value = jersey.secondary;
    secondary.oninput = () => ((jersey.secondary = secondary.value), redraw());
    colorRow.append(div('font-size:12px;color:var(--ink-3)', 'Colours'), primary, secondary);
    const crestRow = div('display:flex;flex-wrap:wrap;gap:2px;margin:8px 0;max-width:100%');
    crestRow.append(div('font-size:12px;color:var(--ink-3);width:100%', 'Crest'));
    for (const cr of CRESTS) crestRow.append(button(cr.slice(6), () => ((jersey.crest = cr), rebuild()), { active: jersey.crest === cr }));
    wrap.append(patternRow, colorRow, crestRow);
    const rebuild = (): void => {
      // re-render the control panel to reflect active-state highlights, then the preview
      wrap.replaceWith(editorControls(jersey, redraw));
      redraw();
    };
    return wrap;
  };

  // ── TEAM DETAIL (members, record, invites, play) ───────────────────────────────
  const teamDetail = (id: string): void => {
    const c = div();
    c.append(title(), note('loading…'));
    mount(c);
    session.api
      .get<{ team: { id: string; name: string; jersey: JerseySpec; ownerAccountId: string }; members: Array<{ accountId: string; role: string }>; record: { played: number; wins: number; draws: number; losses: number; gf: number; ga: number } }>(`/teams/${id}`)
      .then((v) => {
        const body = div();
        body.append(title());
        const head = div('display:flex;gap:12px;align-items:center');
        head.append(createJerseyPreview(v.team.jersey, 64), div('font-size:20px;font-weight:700', v.team.name));
        body.append(head);
        body.append(div('margin:8px 0;font-size:13px;color:var(--ink-2)', `Record: ${v.record.wins}W ${v.record.draws}D ${v.record.losses}L · GF ${v.record.gf} · GA ${v.record.ga} (${v.record.played} played)`));
        body.append(div('margin:6px 0;font-size:13px', `Members: ${v.members.length}`));
        const owner = session.account && v.team.ownerAccountId === session.account.id;
        const msg = note('');
        if (owner) {
          body.append(
            button('Create invite link', () => {
              session.api
                .post<{ code: string }>(`/teams/${id}/invites`, {})
                .then((r) => {
                  const link = `${location.origin}${location.pathname}#join=${r.code}`;
                  void navigator.clipboard?.writeText(link).then(
                    () => (msg.textContent = '✓ invite link copied'),
                    () => (msg.textContent = link),
                  );
                })
                .catch((e) => (msg.textContent = err(e)));
            }, { primary: true }),
          );
        }
        // hand off into a match: close WITHOUT the state-change ping — home must not re-show over the lobby
        body.append(button('🌐 Play as this team', () => (dismiss(), opts.onCreateOnline(id))));
        body.append(msg, div('margin-top:12px'), button('← My teams', () => teams()));
        mount(body);
      })
      .catch((e) => {
        const c2 = div();
        c2.append(title(), note(err(e), 'var(--danger-ink)'), button('← My teams', () => teams()));
        mount(c2);
      });
  };

  // ── boot: honour a magic-link ?token or an #join=CODE deep link (else stay closed) ──
  const boot = (): void => {
    const url = new URL(location.href);
    const magic = url.searchParams.get('token');
    const joinHash = location.hash.startsWith('#join=') ? location.hash.slice(6) : null;
    if (magic) {
      url.searchParams.delete('token');
      history.replaceState(null, '', url); // strip the token from the address bar
      open();
      const c = div();
      c.append(title(), note('verifying sign-in…'));
      mount(c);
      session.refresh().finally(() => verifyToken(magic));
    } else if (session.signedIn) {
      session.refresh().finally(() => {
        if (joinHash) {
          open();
          void session.api.post('/teams/join', { code: joinHash }).finally(() => teams());
        } else {
          opts.onStateChange?.(); // the refresh may have loaded (or 401-cleared) the account
        }
      });
    }
    // signed out with no params: nothing to do — the home screen is already the landing view
  };

  return {
    openSignIn: () => (open(), signIn()),
    openAccount: () => (open(), account()),
    openTeams: () => (open(), teams()),
    boot,
  };
}
